import { getLogger } from '@deltachat-desktop/shared/logger'
import { once } from './mediaMetadata'

const log = getLogger('renderer/utils/videoPoster')

/** Longest edge of the generated poster. Core shrinks it to 128px for the pre-message
 * thumbnail; the rest of the margin is for UIs that show the poster itself. */
const MAX_EDGE = 512

/** A frame is never worth blocking an attachment over — a video that needs longer than
 * this to decode one frame simply gets no poster. */
const TIMEOUT_MS = 5_000

/**
 * Grabs a frame from the video at `url` as base64 JPEG (no `data:` prefix).
 *
 * The browser already ships a decoder for every video it can play, so this needs neither a
 * dependency nor a codec of our own — which is also why it has to happen here and not in
 * core, whose wasm runs in a worker with no DOM.
 *
 * Returns null whenever the browser cannot decode, seek or paint the video (DRM, unsupported
 * codec, timeout): a poster is optional everywhere it is used.
 */
export async function videoPoster(url: string): Promise<string | null> {
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(new Error('timeout')), TIMEOUT_MS)
  const video = document.createElement('video')
  try {
    video.muted = true
    // Safari refuses to decode into a canvas for a video that wants fullscreen.
    video.playsInline = true
    video.preload = 'auto'
    video.src = url
    await once(video, 'loadeddata', abort.signal)

    // A frame from the very start is often black (fade-in, leader), so prefer ~1s in.
    const seekTo = Number.isFinite(video.duration)
      ? Math.min(1, video.duration / 2)
      : 0
    if (seekTo > 0) {
      video.currentTime = seekTo
      await once(video, 'seeked', abort.signal)
    }

    const { videoWidth: w, videoHeight: h } = video
    if (w === 0 || h === 0) {
      return null
    }
    const scale = Math.min(1, MAX_EDGE / Math.max(w, h))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(w * scale)
    canvas.height = Math.round(h * scale)
    const ctx = canvas.getContext('2d')
    if (ctx == null) {
      return null
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/jpeg', 0.7).split(';base64,')[1] ?? null
  } catch (error) {
    log.warn('could not grab a video frame:', error)
    return null
  } finally {
    clearTimeout(timer)
    abort.abort()
    // Drop the decoder and let the browser release the (possibly large) buffered video.
    video.removeAttribute('src')
    video.load()
  }
}
